import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  ApartmentOutlined,
  RobotOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClusterOutlined,
  InboxOutlined,
  DeleteOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { SbTable, SbButton, SbDrawer, SbEmpty, SbSpin, SbBetaTag, SbModal, SbTooltip, SbInput } from "uiCore";
import PageHeader from "Components/PageHeader";
import { toTimestampMs } from "Utils/timeFormat";
import {
  PageTransitionStyled,
  ClickableTableWrapperStyled,
  EntryCardsRowStyled,
  EntryCardStyled,
  EntryIconSlotStyled,
  EntryIconBadgeStyled,
  SectionTitleRowStyled,
  ImportEmptyStyled,
  RowActionsCellStyled,
  RowActionButtonStyled,
  TooltipTargetStyled,
  ArchiveModalTitleStyled,
  ArchiveModalTitleIconStyled,
  ArchiveTargetNameBoxStyled,
  ArchiveWarningBoxStyled,
  ArchiveWarningTextStyled,
  ArchiveConfirmLabelStyled,
  ArchiveConfirmInputStyled,
  ModalFooterActionsStyled,
} from "./orgDesign.styles";
import OrgContextTab from "./components/OrgContextTab";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import {
  usePublishedOrgRedesignVersions,
  useOrgDesignWorkspace,
  useCanAccessCurrentOrgFlow,
  useCanAccessPaicOrgDesignFlow,
  useCanArchiveOrgRedesign,
  useCurrentSbUserId,
} from "./useOrgDesignData";
import {
  TEXT,
  activatableRowProps,
  isVersionOwnedBy,
  TOUR_MODULE_KEY,
  CROSS_PAGE_TOUR_PHASE,
  getTourSteps,
} from "./orgDesign.constants";
// Guided tour — reuses the shared SbTour helpers
import SbTour from "uiCore/SbTour/SbTour";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import useTourStore from "Store/useTourStore";
import { buildVersionColumns, displayProjectName } from "./orgDesignVersionColumns";

const { Title } = Typography;

const ANALYTICS_FROM = "org_design_landing";
const EVENTS = ANALYTICS_EVENTS.ORG_DESIGN;

// The name the user types to confirm an archive (same check as PAIC's
// Archive Group). Falls back to the design code for an unnamed version.
const getArchiveConfirmName = (record) => displayProjectName(record) || record?.sb_org_redesign_code || "";

// The row itself opens the version on click/Enter/Space, so the actions cell
// stops those events before they reach it.
const stopRowActivation = (e) => e.stopPropagation();

const OrgDesignLanding = () => {
  const navigate = useNavigate();
  const { versions, loading, opening, openVersion, archivingId, archiveVersion } = usePublishedOrgRedesignVersions();
  const { startingExistingOrg, startFromExistingOrg } = useOrgDesignWorkspace();
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const canAccessCurrentOrg = useCanAccessCurrentOrgFlow();
  const canAccessPaic = useCanAccessPaicOrgDesignFlow();
  const canArchive = useCanArchiveOrgRedesign();
  const currentSbUserId = useCurrentSbUserId();
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveConfirmName, setArchiveConfirmName] = useState("");
  const track = useAnalytics(ANALYTICS_FROM);

  // Which entry points a user can even see is the thing worth knowing here —
  // an empty Org Design funnel reads very differently when neither card is
  // rendered than when both are and nobody clicks.
  useEffect(() => {
    track(EVENTS.LANDING_VIEWED, { canAccessCurrentOrg, canAccessPaic });
  }, [track, canAccessCurrentOrg, canAccessPaic]);

  // ── Guided tour ────────────────────────────────────────────────────────────
  // Shared tour wiring: controller + seen-flag + auto-launch + global-button
  // registration. Auto-launch is suppressed while a cross-page hop is in flight.
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const clearCrossPageTourPhase = useTourStore((s) => s.clearCrossPageTourPhase);
  const {
    open: tourOpen,
    current: tourStep,
    remountKey: tourRunId,
    begin,
    goToStep: goToTourStep,
    close: closeMainTour,
    markSeen,
  } = useModuleTour({ moduleKey: TOUR_MODULE_KEY, autoLaunch: !crossPageTourPhase });

  const tourTargetRefs = {
    entryCards: useRef(null),
    entryExisting: useRef(null),
    entryPaic: useRef(null),
    versionsTable: useRef(null),
  };

  const handleStartExisting = async () => {
    if (startingExistingOrg) return;
    track(EVENTS.START_FROM_CURRENT_ORG);
    const loaded = await startFromExistingOrg();
    if (loaded) navigate("/org-design/workspace");
  };

  const handleOpenPaicImport = () => {
    track(EVENTS.OPEN_PAIC_IMPORT);
    navigate("/org-design/paic-import");
  };

  const handleOpenVersion = async (record) => {
    if (opening) return;
    track(EVENTS.VERSION_OPENED, {
      orgRedesignId: record.sb_org_redesign_unique_id,
      sourceType: record.source_type,
      version: record.version,
    });
    const opened = await openVersion(record);
    if (opened) navigate("/org-design/workspace");
  };

  const archiving = !!archivingId;
  const archiveTargetName = archiveTarget ? getArchiveConfirmName(archiveTarget) : "";
  const isArchiveConfirmMatch = !!archiveTarget && archiveConfirmName.trim() === archiveTargetName.trim();

  const openArchiveModal = (record) => {
    setArchiveConfirmName("");
    setArchiveTarget(record);
  };

  const closeArchiveModal = () => {
    if (archiving) return;
    setArchiveTarget(null);
    setArchiveConfirmName("");
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget || !isArchiveConfirmMatch || archiving) return;
    const archived = await archiveVersion(archiveTarget);
    if (archived) {
      track(EVENTS.VERSION_ARCHIVED, {
        orgRedesignId: archiveTarget.sb_org_redesign_unique_id,
        sourceType: archiveTarget.source_type,
        version: archiveTarget.version,
      });
      setArchiveTarget(null);
      setArchiveConfirmName("");
    }
  };

  // Archive is owner-only on the backend, so the column only appears for
  // users with org_redesign:delete, and the button only on rows they created.
  const columns = useMemo(() => {
    const base = buildVersionColumns(versions);
    if (!canArchive) return base;
    return [
      ...base,
      {
        title: "Actions",
        key: "actions",
        align: "center",
        width: 80,
        render: (_, record) =>
          isVersionOwnedBy(record, currentSbUserId) ? (
            <RowActionsCellStyled onClick={stopRowActivation} onKeyDown={stopRowActivation}>
              <SbTooltip content="Archive">
                <TooltipTargetStyled>
                  <RowActionButtonStyled
                    type="button"
                    aria-label={`Archive ${displayProjectName(record) || "this design"}`}
                    disabled={archivingId === record.sb_org_redesign_unique_id}
                    onClick={() => openArchiveModal(record)}
                  >
                    <InboxOutlined />
                  </RowActionButtonStyled>
                </TooltipTargetStyled>
              </SbTooltip>
            </RowActionsCellStyled>
          ) : null,
      },
    ];
  }, [versions, canArchive, currentSbUserId, archivingId]);

  // ── Guided tour: branch + handlers ─────────────────────────────────────────
  // The workspace steps show on the workspace (after the hop), not here —
  // advancing past the Previous Iterations step opens the most recent design.
  // Still-loading counts as has-data (same as Jobs Library), so an early
  // auto-launch doesn't lock into the empty branch before versions arrive.
  const branchTourSteps = getTourSteps({ hasVersions: loading || versions.length > 0, canAccessCurrentOrg, canAccessPaic });
  const tourSteps = branchTourSteps.filter((step) => !step.page);

  const closeTour = () => {
    markSeen();
    closeMainTour();
  };

  // Finish greeting primary → bring the two entry cards into focus.
  const finishTour = () => {
    markSeen();
    closeMainTour();
    const cards = tourTargetRefs.entryCards.current;
    cards?.scrollIntoView({ behavior: "smooth", block: "center" });
    cards?.querySelector("[tabindex]")?.focus({ preventScroll: true });
  };

  // Advancing onto a workspace step opens the most recent published design
  // (the table's own default sort) and hands the tour to the workspace. If it
  // can't be opened, the tour carries on here at Finish instead.
  const changeTourStep = async (step) => {
    const nextSpecStep = branchTourSteps[branchTourSteps.indexOf(tourSteps[tourStep]) + 1];
    if (step > tourStep && nextSpecStep?.page === CROSS_PAGE_TOUR_PHASE.WORKSPACE) {
      const [latest] = [...versions].sort(
        (a, b) => toTimestampMs(b.last_updated_time_stamp) - toTimestampMs(a.last_updated_time_stamp)
      );
      closeMainTour();
      setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.WORKSPACE);
      const opened = latest && (await openVersion(latest));
      if (opened) {
        navigate("/org-design/workspace");
      } else {
        clearCrossPageTourPhase();
        begin(tourSteps.length - 1);
      }
      return;
    }
    goToTourStep(step);
  };

  // Returning from the workspace leg → resume at Finish once settled.
  useEffect(() => {
    if (loading) return;
    const resumeAt = tourSteps.findIndex((step) => step.resumePhase && step.resumePhase === crossPageTourPhase);
    if (resumeAt < 0) return;
    clearCrossPageTourPhase();
    begin(resumeAt);
  }, [crossPageTourPhase, loading, tourSteps, clearCrossPageTourPhase, begin]);

  const { steps: builtTourSteps, actionsRender: renderTourActions, indicatorsRender: renderTourIndicators } =
    buildTourSteps(tourSteps, {
      resolveTarget: (step) => tourTargetRefs[step.key]?.current,
      greeting: {
        icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <ClusterOutlined />),
        onPrimary: (step, index, isLast) => (isLast ? finishTour() : changeTourStep(index + 1)),
        onSecondary: () => closeTour(),
      },
    });

  return (
    <PageTransitionStyled>
      <PageHeader
        title={TEXT.PAGE_TITLE}
        subtitle={TEXT.PAGE_DESCRIPTION}
        actions={
          <SbButton
            buttonType="secondary"
            icon={<FileTextOutlined />}
            onClick={() => {
              track(EVENTS.ORG_CONTEXT_OPENED);
              setContextDrawerOpen(true);
            }}
          >
            {TEXT.ORG_CONTEXT_TAB}
            <SbBetaTag label="Coming soon" />
          </SbButton>
        }
      />

      <SectionTitleRowStyled $topGap>
        <Title level={5}>Start a New Organisation Design</Title>
      </SectionTitleRowStyled>
      {canAccessCurrentOrg || canAccessPaic ? (
        <EntryCardsRowStyled ref={tourTargetRefs.entryCards}>
          {canAccessCurrentOrg && (
            <EntryCardStyled
              ref={tourTargetRefs.entryExisting}
              onClick={handleStartExisting}
              $loading={startingExistingOrg}
              {...activatableRowProps(handleStartExisting)}
            >
              <EntryIconSlotStyled>
                {startingExistingOrg ? (
                  <SbSpin size="default" />
                ) : (
                  <EntryIconBadgeStyled>
                    <ApartmentOutlined />
                  </EntryIconBadgeStyled>
                )}
              </EntryIconSlotStyled>
              <div className="entry-title">{TEXT.OPTION_A_TITLE}</div>
              <div className="entry-desc">{startingExistingOrg ? "Loading your organization structure…" : TEXT.OPTION_A_DESC}</div>
            </EntryCardStyled>
          )}
          {canAccessPaic && (
            <EntryCardStyled
              ref={tourTargetRefs.entryPaic}
              onClick={handleOpenPaicImport}
              {...activatableRowProps(handleOpenPaicImport)}
            >
              <EntryIconSlotStyled>
                <EntryIconBadgeStyled>
                  <RobotOutlined />
                </EntryIconBadgeStyled>
              </EntryIconSlotStyled>
              <div className="entry-title">{TEXT.OPTION_B_TITLE}</div>
              <div className="entry-desc">{TEXT.OPTION_B_DESC}</div>
            </EntryCardStyled>
          )}
        </EntryCardsRowStyled>
      ) : (
        <ImportEmptyStyled>
          <SbEmpty description="You don't have access to start a new organization design." />
        </ImportEmptyStyled>
      )}

      <SectionTitleRowStyled $sectionGap>
        <Title level={5}>{TEXT.PREVIOUS_ITERATIONS}</Title>
      </SectionTitleRowStyled>
      {!loading && versions.length === 0 ? (
        <ImportEmptyStyled>
          <SbEmpty description="No published Organisation Designs yet — start a new one above." />
        </ImportEmptyStyled>
      ) : (
        <ClickableTableWrapperStyled ref={tourTargetRefs.versionsTable}>
          <SbTable
            columns={columns}
            dataSource={versions}
            rowKey="sb_org_redesign_unique_id"
            loading={loading || opening}
            sticky
            pagination={{ defaultPageSize: 10, pageSizeOptions: ["10", "20", "30"], showSizeChanger: true }}
            onRow={(record) => ({ onClick: () => handleOpenVersion(record), ...activatableRowProps(() => handleOpenVersion(record)) })}
          />
        </ClickableTableWrapperStyled>
      )}

      <SbDrawer
        open={contextDrawerOpen}
        onClose={() => setContextDrawerOpen(false)}
        title={TEXT.ORG_CONTEXT_TAB}
        width="480px"
      >
        <OrgContextTab />
      </SbDrawer>

      {/* Same layout as PAIC's "Archive Group" modal. The wording is its own:
          archiving is one-way, per version, and keeps the data for audit. */}
      <SbModal
        open={!!archiveTarget}
        title={
          <ArchiveModalTitleStyled>
            <ArchiveModalTitleIconStyled>
              <DeleteOutlined />
            </ArchiveModalTitleIconStyled>
            Archive this version?
          </ArchiveModalTitleStyled>
        }
        onCancel={closeArchiveModal}
        closable={!archiving}
        maskClosable={!archiving}
        keyboard={!archiving}
        centered
        width={520}
        footer={
          <ModalFooterActionsStyled>
            <SbButton buttonType="default" onClick={closeArchiveModal} disabled={archiving}>
              Cancel
            </SbButton>
            <SbButton buttonType="red" onClick={handleArchiveConfirm} disabled={!isArchiveConfirmMatch || archiving}>
              {archiving ? "Archiving..." : "Archive Version"}
            </SbButton>
          </ModalFooterActionsStyled>
        }
      >
        <div>You are about to archive this version:</div>
        <ArchiveTargetNameBoxStyled>{archiveTargetName}</ArchiveTargetNameBoxStyled>
        <ArchiveWarningBoxStyled>
          <WarningFilled />
          <ArchiveWarningTextStyled>
            This version will be archived and removed from the active list. You won’t be able to view or restore
            it after archiving.
          </ArchiveWarningTextStyled>
        </ArchiveWarningBoxStyled>
        <ArchiveConfirmLabelStyled>To confirm, type the version name exactly:</ArchiveConfirmLabelStyled>
        <ArchiveConfirmInputStyled>
          <SbInput
            placeholder="Type the name to confirm..."
            value={archiveConfirmName}
            disabled={archiving}
            onChange={(e) => setArchiveConfirmName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isArchiveConfirmMatch) handleArchiveConfirm();
            }}
          />
        </ArchiveConfirmInputStyled>
      </SbModal>

      {/* ── Guided tour ── */}
      <SbTour
        key={tourRunId}
        open={tourOpen}
        current={tourStep}
        onChange={changeTourStep}
        steps={builtTourSteps}
        onClose={closeTour}
        actionsRender={renderTourActions}
        indicatorsRender={renderTourIndicators}
      />
    </PageTransitionStyled>
  );
};

export default OrgDesignLanding;
